import React, {
  useRef,
  useState,
  useMemo,
  useCallback,
  memo,
  useEffect,
  useContext
} from 'react';
import { AgGridReact } from 'ag-grid-react';
import type {
  ColDef,
  DefaultMenuItem,
  FirstDataRenderedEvent,
  GetContextMenuItemsParams,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  MenuItemDef,
  ProcessCellForExportParams,
  SortDirection,
  ToolPanelDef
} from 'ag-grid-community';
import type { AgGridReactProps } from 'ag-grid-react';
import { useSelector } from 'react-redux';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setFilters } from '@/app/common/reducers/filterSlice';
import { usePostTableSettingsMutation } from '@/app/common/api/tableApi';
import {
  successMessage,
  errorMessage,
  warningMessage,
  defaultMessage
} from '@/utils/toast';
import {
  convertToQueryString,
  isPresentQueryValue
} from '@/utils/queryString';
import { createColumnsDef } from '@/app/common/utils/createColumnsDef';
import { processCellForClipboard, valueFormatter } from './configs';

import AgGridLoading from './AgGridLoading';
import AgGridButtons from './AgGridButtons';
import AppContext, {
  RemoveAnalysisContext,
  RemoveCrawlContext,
  RemoveSerpContext
} from '@/context/Context';
import AG_GRID_LOCALE_FA from '@/locales/AG_GRID_LOCALE_FA';
import AgGridMasterDetails from './AgGridMasterDetails';
import ExportHelpAlert from '../../../../components/common/ExportHelpAlert';
import useDomain from '@/hooks/useDomain';
import { breakpointsMediaQuery, useMediaQuery } from '@/helpers/utils';
import AgGridHeader from './AgGridHeader';
import AgGridGroupHeader from './AgGridGroupHeader';
import {
  assignForwardedRef,
  type AgGridColumnDef,
  type AgGridExportRequest,
  type AgGridExportResponse,
  type AgGridFetchResponse,
  type AgGridHeight,
  type AgGridRowData,
  type AgGridSidebarConfig,
  type AgGridViewRef,
  type AgGridWrapperProps
} from './types';
import type { FiltersRootState } from '../filters/filterTypes';

interface PreviousPathState {
  path: string;
  query: string;
}

const AgGridWrapper = React.forwardRef<
  AgGridReact<AgGridRowData>,
  AgGridWrapperProps
>(
  (
    {
      table_name,
      forceDir,
      forceFilters = null,
      columns = [],
      otherButtons,
      data,
      height = '500px',
      otherOptions = {},
      fetchFunction = null,
      additionalData = {},
      additionalContext = [],

      hideFooter = false,
      hideExportHelpAlert = false,
      stickyHorizontalScroll = false,
      stickyHeader = false,
      total,
      perPage = 50,
      page_depth = 1 /* عمق صفحه */,
      settings = {
        pagination: true,
        fullscreen: false,
        changeHeight: false,
        saveGrid: false,
        resetGrid: false,
        sideBar: false,
        masterDetail: null,
        export: {
          enabled: false,
          fetchFunction: null,
          additionalData: null
        }
      }
    },
    ref
  ) => {
    const isMdUp = useMediaQuery(breakpointsMediaQuery.md);
    const exportMsg =
      'خروجی شما در حال پردازش است، پس از اتمام در بخش نوتیفیکیشن میتونید فایل رو دانلود کنید..';
    const { domain_slug } = useDomain();
    const { slug } = useParams();
    const job_page = slug === 'last' ? domain_slug : slug;
    const dispatch = useDispatch();

    // این خط چک میکند که اگر ref داشتیم به wrapper اضافه کند و اگر نداشتیم پیش فرض قرار دهد .
    const gridRef = useRef<AgGridReact<AgGridRowData> | null>(null);
    const setGridRef = useCallback(
      (instance: AgGridReact<AgGridRowData> | null) => {
        gridRef.current = instance;
        assignForwardedRef(ref, instance);
      },
      [ref]
    );
    const paramsRef = useRef<ReturnType<typeof useParams> | null>(null);
    const viewRef = useRef<HTMLDivElement | null>(null);
    const heightStateBackUp = useRef<AgGridHeight | undefined>(undefined);
    const params = useParams();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [heightState, setHeightState] = useState<AgGridHeight>(height);
    const [forceResteFilters, setForceResetFilters] = useState(false);
    const [postTable, { isLoading: isSavingState }] =
      usePostTableSettingsMutation();
    const filters = forceFilters
      ? forceFilters
      : useSelector((state: FiltersRootState) => state.filters);
    const {
      config: { isRTL, theme }
    } = useContext(AppContext);
    let searchQuery = '';

    useEffect(() => {
      const handleFullscreenChange = () => {
        if (!document.fullscreenElement) {
          setHeightState(heightStateBackUp.current || heightState);
        }
      };

      document.addEventListener('fullscreenchange', handleFullscreenChange);

      return () => {
        document.removeEventListener(
          'fullscreenchange',
          handleFullscreenChange
        );
      };
    }, []);

    // Sticky table header must sit below the fixed top navbar — measure real height.
    useEffect(() => {
      if (!stickyHeader) return undefined;

      const updateStickyHeaderOffset = () => {
        const nav = document.querySelector('.navbar-top');
        const offset = nav
          ? Math.ceil(nav.getBoundingClientRect().height) + 2
          : 69;
        const value = `${offset}px`;
        document.documentElement.style.setProperty(
          '--ag-sticky-header-offset',
          value
        );
        if (viewRef.current) {
          viewRef.current.style.setProperty('--ag-sticky-header-offset', value);
        }
      };

      updateStickyHeaderOffset();
      window.addEventListener('resize', updateStickyHeaderOffset);
      const nav = document.querySelector('.navbar-top');
      const resizeObserver =
        nav && typeof ResizeObserver !== 'undefined'
          ? new ResizeObserver(updateStickyHeaderOffset)
          : null;
      if (resizeObserver && nav) {
        resizeObserver.observe(nav);
      }

      return () => {
        window.removeEventListener('resize', updateStickyHeaderOffset);
        resizeObserver?.disconnect();
      };
    }, [stickyHeader]);

    /* 
      این بخش برای ری لود شدن مجدد جدول بعد از اعمال فیلتر است
      details=internal => برای جدا کردن کویری استرینگ صفحات و صفحاتی ک در دراور باز میشن 
      کاری کردم که وقتی صفحات دراور باز میشن جدول های صفحه اول یا صفحات قبل مجددا رندر نشن
    */
    if (
      !forceFilters &&
      location.search.split('details=internal')[page_depth - 1].includes('FBy')
    ) {
      searchQuery = location.search.split('details=internal')[page_depth - 1]; // گرفتن کویری استرینگ هر صفحه بر اساس عمق
      searchQuery = searchQuery.startsWith('?')
        ? searchQuery.slice(0, searchQuery.length - 1)
        : searchQuery; // حذف علامت سوال از ابتدای کویری
      searchQuery = searchQuery.endsWith('&')
        ? searchQuery.slice(0, -1)
        : searchQuery; // حذف علامت & از انتهای آدرس
    } else {
      const issues = searchParams.get('issues');
      if (isPresentQueryValue(issues)) {
        // برای فهرست بازبینی خزش
        searchQuery = issues ?? '';
      }
    }

    useEffect(() => {
      paramsRef.current = params;
    }, [params]);

    const loadingCellRenderer = useMemo(() => AgGridLoading, []);

    /* AG Grid Columns */  
    const columnsDef = useMemo<AgGridColumnDef[]>(
      () =>
        createColumnsDef(
          columns as Parameters<typeof createColumnsDef>[0]
        ) as AgGridColumnDef[],
      settings?.saveGrid ? [] : [columns]
    );

    /* AG Grid Components */
    const components = useMemo(
      () => ({
        agColumnHeader: AgGridHeader,
        agColumnGroupHeader: AgGridGroupHeader
      }),
      []
    );
    /* AG Grid Loading Message */
    const loadingCellRendererParams = useMemo(
      () => ({ loadingMessage: ' درحال بارگذاری اطلاعات...' }),
      []
    );

    /* دریافت اطلاعات جدول از سمت سرور */
    const createServerSideDatasource = (): IServerSideDatasource<AgGridRowData> => {
      const serverFetchFunction = fetchFunction;
      return {
        getRows: async (params: IServerSideGetRowsParams<AgGridRowData>) => {
          const previousPath = location.state as PreviousPathState | null; // دریافت مسیر قبلی کاربر
          const storedFilters = settings?.columnsState?.filters;
          const saveFilters =
            !forceResteFilters && storedFilters
              ? Object.keys(storedFilters).length !== 0
                ? storedFilters
                : null
              : null;

          let newFilters: Record<string, unknown> =
            Object.keys(filters).length !== 0
              ? { ...filters }
              : { ...(saveFilters ?? {}) };

          if (previousPath) {
            //اگر وارد مسیر جدیدی شده بود
            if (
              previousPath.path !== location.pathname || //اگر از صفحه یا تبی وارد صفحه دیگر شد
              !searchParams.toString().includes(previousPath.query) // اگر درون یک صفحه مثل چک لیست بود و به آدرس های مختلف میرفت
            ) {
              newFilters = {};
              dispatch(setFilters({}));
            }
          }

          const { sortModel, endRow } = params.request;
          const page = (endRow ?? perPage) / perPage;
          const body_data: Record<string, unknown> = {
            perPage: perPage,
            page,
            filters: newFilters,
            ...additionalData
          };

          if (sortModel.length > 0)
            body_data.sort = {
              column: sortModel[0].colId,
              order: sortModel[0].sort
            };

          if (
            data &&
            page === 1 &&
            Object.keys(filters).length === 0 &&
            !saveFilters &&
            sortModel.length == 0
          ) {
            params.success({
              rowData: data as unknown as AgGridRowData[],
              rowCount: total
            });
          } else if (serverFetchFunction) {
            await serverFetchFunction(body_data)
              .unwrap()
              .then(response => {
                const { rows, total: total_entities } = (
                  response as AgGridFetchResponse
                ).data;
                params.success({
                  rowData: rows,
                  rowCount: total_entities
                });
              })
              .catch(err => {
                console.error('Error fetching data:', err);
                params.fail();
              });
          } else {
            params.fail();
          }
        }
      };
    };

    const serverSideDatasource = useMemo(() => {
      return createServerSideDatasource();
    }, [paramsRef, searchQuery, data]); // وا

    /* Master Details */
    const detailCellRenderer = useMemo(() => AgGridMasterDetails, []);

    const {
      setJobPage: setRemJobPage,
      setStart_url: setRemStartUrl,
      setShow
    } = useContext(RemoveCrawlContext);
    const {
      setSerpId: setRemSerpId,
      setKeyword: setRemKeyword,
      setShow: setSerpRemShow
    } = useContext(RemoveSerpContext);
    const {
      setAnalysisId: setRemAnalysisId,
      setAnalysisUrl: setRemAnalysisUrl,
      setShow: setRemAnalysisShow
    } = useContext(RemoveAnalysisContext);
    /* فیلتر کردن و شخصی سازی کلیک راست جدول */
    const getContextMenuItems = useCallback(
      (params: GetContextMenuItemsParams<AgGridRowData>) => {
      if (params.defaultItems) {
        const rowData = params.node?.data ?? {};
        const defaultMenuItems = params.defaultItems.slice();
        const filteredItems = defaultMenuItems.filter(function (item) {
          return (
            item !== 'export' &&
            item !== 'copyWithGroupHeaders' &&
            item !== 'cut' &&
            item !== 'paste' &&
            item !== 'separator'
          );
        });
        const sorts = params.api
          .getColumnState()
          .filter(item => item.sort !== null);
        const columnsArr = gridRef.current?.api.getAllDisplayedColumns() ?? [];
        const columnsObject: Record<
          string,
          { title: string; width: number }
        > = {};
        columnsArr.forEach(column => {
          const columnDefinition = column.getUserProvidedColDef();
          columnsObject[column.getColId()] = {
            title: columnDefinition?.headerName ?? '',
            width: column.getActualWidth()
          };
        });
        const finalResult: (
          | DefaultMenuItem
          | MenuItemDef<AgGridRowData>
        )[] = [
          ...filteredItems,
          {
            name: 'تمام صفحه',
            icon: '<svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="expand-arrows-alt" class="svg-inline--fa fa-expand-arrows-alt fa-w-14 fs--1" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path fill="currentColor" d="M448 344v112a23.94 23.94 0 0 1-24 24H312c-21.39 0-32.09-25.9-17-41l36.2-36.2L224 295.6 116.77 402.9 153 439c15.09 15.1 4.39 41-17 41H24a23.94 23.94 0 0 1-24-24V344c0-21.4 25.89-32.1 41-17l36.19 36.2L184.46 256 77.18 148.7 41 185c-15.1 15.1-41 4.4-41-17V56a23.94 23.94 0 0 1 24-24h112c21.39 0 32.09 25.9 17 41l-36.2 36.2L224 216.4l107.23-107.3L295 73c-15.09-15.1-4.39-41 17-41h112a23.94 23.94 0 0 1 24 24v112c0 21.4-25.89 32.1-41 17l-36.19-36.2L263.54 256l107.28 107.3L407 327.1c15.1-15.2 41-4.5 41 16.9z"></path></svg>',
            action: () => {
              if (!document.fullscreenElement) {
                setHeightState(prev => {
                  heightStateBackUp.current = prev;
                  return '100vh';
                });
                viewRef.current?.requestFullscreen();
              } else {
                document.exitFullscreen();
              }
            }
          }
        ];

        const exportSettings = settings.export;
        if (exportSettings?.enabled) {
          finalResult.push('separator', {
            name: 'خروجی گرفتن',
            icon: '<span class="ag-icon ag-icon-save" unselectable="on" role="presentation"></span>',
            subMenu: [
              {
                icon: '<span class="ag-icon ag-icon-excel" unselectable="on" role="presentation"></span>',
                name: 'خروجی از صفحه',
                disabled: false,
                action: () => {
                  let bodyReq: AgGridExportRequest = {
                    domain_slug,
                    job_page,
                    table_name,
                    filters,
                    offset: params.api.paginationGetCurrentPage() * perPage,
                    limit: perPage,
                    columns: columnsObject,
                    export_type: 'excel'
                  };

                  if (exportSettings.additionalData) {
                    bodyReq = {
                      ...bodyReq,
                      ...exportSettings.additionalData
                    };
                  }

                  if (sorts.length > 0) {
                    bodyReq.sort = {
                      column: sorts[0].colId,
                      order: sorts[0].sort
                    };
                  }
                  const exportResult = exportSettings.fetchFunction?.(bodyReq);
                  if (exportResult) {
                    Promise.resolve(exportResult)
                      .then(res => {
                        if ((res as AgGridExportResponse)?.data?.data === 429) {
                          warningMessage(
                            'درخواست شما تکراری است، لطفا منتظر پردازش درخواست قبلی باشید.'
                          );
                        } else {
                          defaultMessage(exportMsg);
                        }
                      })
                      .catch(err => {
                        console.error('export excel err:', err);
                      });
                  }
                }
              },
              {
                name: 'خروجی کل',
                disabled: false,
                icon: '<span class="ag-icon ag-icon-excel" unselectable="on" role="presentation"></span>',
                action: () => {
                  let bodyReq: AgGridExportRequest = {
                    domain_slug,
                    job_page,
                    table_name,
                    filters,
                    offset: 0,
                    limit: 1000000,
                    columns: columnsObject,

                    export_type: 'excel'
                  };

                  if (exportSettings.additionalData) {
                    bodyReq = {
                      ...bodyReq,
                      ...exportSettings.additionalData
                    };
                  }

                  if (sorts.length > 0) {
                    bodyReq.sort = {
                      column: sorts[0].colId,
                      order: sorts[0].sort
                    };
                  }
                  const exportResult = exportSettings.fetchFunction?.(bodyReq);
                  if (exportResult) {
                    Promise.resolve(exportResult)
                      .then(res => {
                        if ((res as AgGridExportResponse)?.data?.data === 429) {
                          warningMessage(
                            'درخواست شما تکراری است، لطفا منتظر پردازش درخواست قبلی باشید.'
                          );
                        } else {
                          defaultMessage(exportMsg);
                        }
                      })
                      .catch(err => {
                        console.error('export excel err:', err);
                      });
                  }
                }
              }
            ]
          });
        } else {
          finalResult.push('separator', 'export');
        }
        if (additionalContext.length > 0)
          finalResult.push(...additionalContext);
        if (location.pathname.includes('/crawl/history'))
          finalResult.push('separator', {
            name: 'حذف',
            icon: '<svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="trash-can" class="svg-inline--fa fa-trash-can fs-0" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path fill="currentColor" d="M135.2 17.7C140.6 6.8 151.7 0 163.8 0L284.2 0c12.1 0 23.2 6.8 28.6 17.7L320 32l96 0c17.7 0 32 14.3 32 32s-14.3 32-32 32L32 96C14.3 96 0 81.7 0 64S14.3 32 32 32l96 0 7.2-14.3zM32 128l384 0 0 320c0 35.3-28.7 64-64 64L96 512c-35.3 0-64-28.7-64-64l0-320zm96 64c-8.8 0-16 7.2-16 16l0 224c0 8.8 7.2 16 16 16s16-7.2 16-16l0-224c0-8.8-7.2-16-16-16zm96 0c-8.8 0-16 7.2-16 16l0 224c0 8.8 7.2 16 16 16s16-7.2 16-16l0-224c0-8.8-7.2-16-16-16zm96 0c-8.8 0-16 7.2-16 16l0 224c0 8.8 7.2 16 16 16s16-7.2 16-16l0-224c0-8.8-7.2-16-16-16z"></path></svg>',
            action: () => {
              setRemJobPage(rowData.job_page as string);
              setRemStartUrl(rowData.start_url as string);
              setShow(true);
            },
            cssClasses: ['text-danger']
          });
        if (location.pathname.includes('/serp/history'))
          finalResult.push('separator', {
            name: 'حذف',
            icon: '<svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="trash-can" class="svg-inline--fa fa-trash-can fs-0" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path fill="currentColor" d="M135.2 17.7C140.6 6.8 151.7 0 163.8 0L284.2 0c12.1 0 23.2 6.8 28.6 17.7L320 32l96 0c17.7 0 32 14.3 32 32s-14.3 32-32 32L32 96C14.3 96 0 81.7 0 64S14.3 32 32 32l96 0 7.2-14.3zM32 128l384 0 0 320c0 35.3-28.7 64-64 64L96 512c-35.3 0-64-28.7-64-64l0-320zm96 64c-8.8 0-16 7.2-16 16l0 224c0 8.8 7.2 16 16 16s16-7.2 16-16l0-224c0-8.8-7.2-16-16-16zm96 0c-8.8 0-16 7.2-16 16l0 224c0 8.8 7.2 16 16 16s16-7.2 16-16l0-224c0-8.8-7.2-16-16-16zm96 0c-8.8 0-16 7.2-16 16l0 224c0 8.8 7.2 16 16 16s16-7.2 16-16l0-224c0-8.8-7.2-16-16-16z"></path></svg>',
            action: () => {
              setRemSerpId(rowData.id as string);
              setRemKeyword(rowData.keyword as string);
              setSerpRemShow(true);
            },
            cssClasses: ['text-danger']
          });
        if (location.pathname.includes('/page-speed/history'))
          finalResult.push('separator', {
            name: 'حذف',
            icon: '<svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 1024 1024" class="fs-0" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path d="M360 184h-8c4.4 0 8-3.6 8-8v8h304v-8c0 4.4 3.6 8 8 8h-8v72h72v-80c0-35.3-28.7-64-64-64H352c-35.3 0-64 28.7-64 64v80h72v-72zm504 72H160c-17.7 0-32 14.3-32 32v32c0 4.4 3.6 8 8 8h60.4l24.7 523c1.6 34.1 29.8 61 63.9 61h454c34.2 0 62.3-26.8 63.9-61l24.7-523H888c4.4 0 8-3.6 8-8v-32c0-17.7-14.3-32-32-32zM731.3 840H292.7l-24.2-512h487l-24.2 512z"></path></svg>',
            action: () => {
              setRemAnalysisId(rowData._id as string);
              setRemAnalysisUrl(rowData.url as string);
              setRemAnalysisShow(true);
            },
            cssClasses: ['text-danger']
          });

        return finalResult;
      }
      return [];
      },
      [additionalContext, data, heightState, location.pathname, settings]
    );

    /* حذف قابلیت های غیرضروری جدول */
    const defaultColDef = useMemo<ColDef<AgGridRowData>>(() => {
      return {
        // DECODER
        // valueFormatter: p => (p?.value ? decodeURIComponent(p.value) : ''),
        cellDataType: false,
        valueFormatter: valueFormatter,
        filter: false,
        sortingOrder: ['desc', 'asc', null] as SortDirection[],
        suppressHeaderMenuButton: false,
        floatingFilter: false,
        resizable: true,
        sortable: true
      };
    }, []);

    /* ذخیره تنظیمات جدول */
    const saveColumnsState = async () => {
      const api = gridRef.current?.api;
      if (!api) return;
      const columns = api.getColumnState();
      window.colState = columns;
      await postTable({
        domain_slug: params.domain_slug,
        table_name: location.pathname.split('/').slice(1, 3).join('/'),
        filters,
        columns
      })
        .unwrap()
        .then(_ => {
          successMessage('ذخیره تنظیمات با موفقیت انجام شد.');
        })
        .catch(err => {
          console.log(err);
          errorMessage('در ذخیره تنظیمات خطایی رخ داده است.');
        });
    };

    /* ریست تنظیمات جدول */
    const resetColumnsState = useCallback(() => {
      const api = gridRef.current?.api;
      if (!api) return;
      api.setFilterModel(null);
      api.resetColumnState(); // ریست تنظیمات ستون ها
      dispatch(setFilters({})); // ریست فیلترهای اعمال شده
      setForceResetFilters(true); // کاری کردیم ک بتونیم فلیترهای دخیره شده رو نادیده بگیریم
      createServerSideDatasource(); // دریافت مجدد داده ها بدون سورت و فیلتر
      setSearchParams(''); // پاک کردن فیلترها از آدرس
    }, []);

    /* اعمال تنظیمات جدول */
    const onFirstDataRendered = useCallback(
      (params: FirstDataRenderedEvent<AgGridRowData>) => {
      if (settings?.columnsState) {
        const tableSettings = settings?.columnsState;
        if (tableSettings.filters && !location.search.includes('FBy')) {
          let queryString = convertToQueryString(tableSettings.filters);
          queryString = `${queryString}`;
          setSearchParams(queryString);
        }
        if (tableSettings.columns) {
          window.colState = tableSettings.columns;
          params.api.applyColumnState({
            state: window.colState,
            applyOrder: true
          });
        }
      }
      },
      [location.search, setSearchParams, settings]
    );

    /* AG Grid props */
    let defaultAgProps: AgGridReactProps<AgGridRowData> = {
      suppressRowDrag: !isMdUp,
      suppressDragLeaveHidesColumns: !isMdUp,
      suppressMovableColumns: !isMdUp,
      processCellForClipboard: (params: ProcessCellForExportParams<AgGridRowData>) =>
        processCellForClipboard(params, location.pathname),
      defaultExcelExportParams: {
        sheetName: 'export-from-mizfa-tools',
        author: 'mizfa-tools',
        fileName: 'export-from-mizfa-tools',
        processCellCallback(params) {
          return processCellForClipboard(params, location.pathname) ?? '';
        }
      },
      defaultCsvExportParams: {
        fileName: 'export-from-mizfa-tools',
        processCellCallback(params) {
          return processCellForClipboard(params, location.pathname) ?? '';
        }
      },
      loadingCellRenderer: loadingCellRenderer,
      loadingCellRendererParams,
      masterDetail: settings.masterDetail ? true : false,
      detailCellRendererParams: {
        masterDetailData: settings.masterDetail
      },
      domLayout: height === 'auto' ? 'autoHeight' : undefined,
      cellSelection: true,
      getContextMenuItems: getContextMenuItems,
      tooltipShowDelay: 0,
      tooltipHideDelay: 5000,
      pagination: settings.pagination,
      paginationPageSize: perPage,
      ...(fetchFunction ? { cacheBlockSize: perPage } : {}),
      suppressRowTransform: true,
      columnDefs: columnsDef,
      suppressColumnVirtualisation: true,
      suppressRowVirtualisation: true,
      ...(fetchFunction
        ? {}
        : { rowData: data as unknown as AgGridRowData[] }),
      enableRtl: forceDir ? (forceDir === 'rtl' ? true : false) : isRTL,
      localeText: forceDir
        ? forceDir === 'rtl'
          ? AG_GRID_LOCALE_FA
          : undefined
        : isRTL
          ? AG_GRID_LOCALE_FA
          : undefined,
      defaultColDef: defaultColDef,
      rowModelType: fetchFunction ? 'serverSide' : 'clientSide',
      serverSideDatasource: fetchFunction ? serverSideDatasource : undefined,
      onFirstDataRendered,
      detailCellRenderer,
      detailRowHeight: 340,
      paginationAutoPageSize: false,
      paginationPageSizeSelector: false,
      statusBar: {
        statusPanels: settings.pagination
          ? [
              {
                statusPanel: 'agAggregationComponent',
                align: 'left',
                statusPanelParams: {
                  aggFuncs: ['avg', 'sum', 'count', 'max', 'min']
                }
              }
            ]
          : []
      },
      ...otherOptions
    };

    if (settings?.sideBar) {
      let filteredSideBarProps: AgGridSidebarConfig;
      const toolPanels: ToolPanelDef[] = [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressPivotMode: true,
            suppressRowGroups: true,
            suppressValues: true
          }
        },
        {
          id: 'filters',
          labelDefault: 'Filters',
          labelKey: 'filters',
          iconKey: 'filter',
          toolPanel: 'agFiltersToolPanel'
        }
      ];
      const sidebarProp: AgGridSidebarConfig = {
        sideBar: {
          toolPanels
        }
      };
      if (fetchFunction) {
        filteredSideBarProps = {
          ...sidebarProp,
          sideBar: {
            ...sidebarProp.sideBar,
            toolPanels: toolPanels.filter(
              item => item.id !== 'filters'
            )
          }
        };
      } else {
        filteredSideBarProps = sidebarProp;
      }
      defaultAgProps = { ...defaultAgProps, ...filteredSideBarProps };
    }
    return (
      <>
        {!hideExportHelpAlert && <ExportHelpAlert />}
        <AgGridButtons
          settings={settings}
          heightState={heightState}
          setHeightState={setHeightState}
          saveColumnsState={saveColumnsState}
          isSavingState={isSavingState}
          resetColumnsState={resetColumnsState}
          heightStateBackUp={heightStateBackUp}
          ref={viewRef}
          otherButtons={otherButtons}
        />
        <div
          ref={viewRef}
          className={`${stickyHeader ? '' : 'overflow-hidden'} ${
            theme === 'dark' ? 'ag-theme-balham-dark' : 'ag-theme-balham'
          } ag-theme-custom ${stickyHeader ? 'ag-grid-sticky-header' : ''}`}
          style={{
            height:
              defaultAgProps.domLayout === 'autoHeight'
                ? undefined
                : heightState,
            width: '100%'
          }}
        >
          <AgGridReact<AgGridRowData>
            ref={setGridRef}
            {...defaultAgProps}
            components={components}
            suppressDragLeaveHidesColumns={true}
            suppressMoveWhenColumnDragging={true}
          />
          {!hideFooter && <div className="ag-footer ag-paging-panel" />}
        </div>
        {stickyHorizontalScroll && (
          <StickyHorizontalScrollbar gridRef={viewRef} />
        )}
      </>
    );
  }
);

const StickyHorizontalScrollbar = ({
  gridRef
}: {
  gridRef: AgGridViewRef;
}) => {
  const scrollbarRef = useRef<HTMLDivElement | null>(null);
  const scrollbarContentRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const gridElement = gridRef.current;
    const scrollbar = scrollbarRef.current;
    const scrollbarContent = scrollbarContentRef.current;
    if (!gridElement || !scrollbar || !scrollbarContent) return undefined;

    let horizontalViewport: HTMLElement | null = null;
    const syncScrollbar = () => {
      const nextViewport = gridElement.querySelector<HTMLElement>(
        '.ag-body-horizontal-scroll-viewport'
      );
      if (nextViewport !== horizontalViewport) {
        horizontalViewport?.removeEventListener('scroll', handleGridScroll);
        horizontalViewport = nextViewport;
        horizontalViewport?.addEventListener('scroll', handleGridScroll);
      }

      const gridRect = gridElement.getBoundingClientRect();
      scrollbar.style.left = `${gridRect.left}px`;
      scrollbar.style.width = `${gridRect.width}px`;

      if (!horizontalViewport) {
        setIsVisible(false);
        return;
      }

      scrollbarContent.style.width = `${horizontalViewport.scrollWidth}px`;
      setIsVisible(
        horizontalViewport.scrollWidth > horizontalViewport.clientWidth &&
          gridRect.bottom > 0 &&
          gridRect.top < window.innerHeight
      );
      scrollbar.scrollLeft = horizontalViewport.scrollLeft;
    };

    const handleGridScroll = () => {
      if (horizontalViewport) {
        scrollbar.scrollLeft = horizontalViewport.scrollLeft;
      }
    };
    const handleStickyScroll = () => {
      if (horizontalViewport) {
        horizontalViewport.scrollLeft = scrollbar.scrollLeft;
      }
    };

    scrollbar.addEventListener('scroll', handleStickyScroll);
    window.addEventListener('scroll', syncScrollbar, { passive: true });
    window.addEventListener('resize', syncScrollbar);
    const mutationObserver = new MutationObserver(syncScrollbar);
    mutationObserver.observe(gridElement, { childList: true, subtree: true });
    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(syncScrollbar)
        : null;
    resizeObserver?.observe(gridElement);
    syncScrollbar();

    return () => {
      scrollbar.removeEventListener('scroll', handleStickyScroll);
      window.removeEventListener('scroll', syncScrollbar);
      window.removeEventListener('resize', syncScrollbar);
      horizontalViewport?.removeEventListener('scroll', handleGridScroll);
      mutationObserver.disconnect();
      resizeObserver?.disconnect();
    };
  }, [gridRef]);

  return (
    <div
      ref={scrollbarRef}
      className="ag-grid-sticky-horizontal-scroll"
      style={{ visibility: isVisible ? 'visible' : 'hidden' }}
      aria-hidden={!isVisible}
    >
      <div ref={scrollbarContentRef} />
    </div>
  );
};

export default memo(AgGridWrapper);
